import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import BodyWrapper from "../../../components/Wrapper/BodyWrapper";
import Header from "../../../components/Header/Header";
import CustomBtn from "../../../components/Buttons/CustomBtn";
import FetchingLoading from "../../../components/Loaders/FetchingLoading";
import AddLoanModal from "../../../components/Modals/AddLoanModal";
import AddLoanPaymentModal from "../../../components/Modals/AddLoanPaymentModal";
import EditLoanModal from "../../../components/Modals/EditLoanModal";
import EditLoanDisbursementModal from "../../../components/Modals/EditLoanDisbursementModal";
import EditLoanPaymentModal from "../../../components/Modals/EditLoanPaymentModal";
import DeleteModal from "../../../components/Modals/DeleteModal";
import LoanTransactionLogCards from "../../../components/Loan/LoanTransactionLogCards";
import CustomTabs from "../../../components/Tabs/CustomTabs";
import AuthInput from "../../../components/Input/AuthInput";
import {
  DeleteLoanApi,
  DeleteLoanPaymentApi,
  GetLoanByIdApi,
} from "../../../ApiRequests";
import { ErrorToast, SuccessToast } from "../../../utils/ShowToast";
import {
  computeLoanRunningBalances,
  getLoanBalance,
  getLoanPaid,
  getLoanPrincipal,
  getLoanStatusLabel,
  getLoanTypeLabel,
} from "../../../utils/LoanConverter";
import { fetchLoanLog, fetchLoans } from "../../../store/Slices/LoanSlice";
import { useDispatch } from "react-redux";
import { isReadOnlyLoanViewer } from "../../../utils/auth";
import { fetchAccounts } from "../../../store/Slices/AccountSlice";

const fmtRs = (n) =>
  Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 });

const DETAIL_TABS = [{ label: "Full log" }, { label: "Loans given only" }, { label: "Repayments only" }];

const LoanDetail = () => {
  const { loanId } = useParams();
  const dispatch = useDispatch();
  const [account, setAccount] = useState(null);
  const [disbursements, setDisbursements] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [openAddLoanModal, setOpenAddLoanModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [deletePayment, setDeletePayment] = useState(null);
  const [deleteDisbursement, setDeleteDisbursement] = useState(null);
  const [editDisbursement, setEditDisbursement] = useState(null);
  const [editRepayment, setEditRepayment] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [logSearch, setLogSearch] = useState("");

  const readOnly = isReadOnlyLoanViewer();

  const loadLoan = useCallback(async () => {
    if (!loanId) return;
    setLoading(true);
    try {
      const res = await GetLoanByIdApi(loanId);
      const payload = res?.data?.data?.payload;
      setAccount(payload?.account || payload?.loan || null);
      setDisbursements(
        Array.isArray(payload?.disbursements) ? payload.disbursements : []
      );
      setPayments(Array.isArray(payload?.payments) ? payload.payments : []);
    } catch (e) {
      setAccount(null);
      setDisbursements([]);
      setPayments([]);
      ErrorToast(e.response?.data?.error?.msg || "Could not load loan account");
    } finally {
      setLoading(false);
    }
  }, [loanId]);

  useEffect(() => {
    loadLoan();
  }, [loadLoan]);

  const logEntries = useMemo(() => {
    const givenRows = disbursements.map((row) => ({
      _id: `d-${row._id}`,
      entryType: "disbursement",
      sourceId: row._id,
      loanAccountId: account?._id,
      partyName: account?.partyName,
      phone: account?.phone,
      loanType: account?.loanType,
      amount: row.amount,
      date: row.date,
      dueDate: row.dueDate,
      accountName: row.accountId?.account_name || "—",
      accountId: row.accountId,
      branchId: row.branchId || account?.branchId,
      marka: row.marka || "",
      bichak_no: row.bichak_no || "",
      desc: row.desc || "",
    }));
    const payRows = payments.map((row) => ({
      _id: `p-${row._id}`,
      entryType: "repayment",
      sourceId: row._id,
      loanAccountId: account?._id,
      partyName: account?.partyName,
      phone: account?.phone,
      loanType: account?.loanType,
      amount: row.amount,
      date: row.date,
      accountName: row.accountId?.account_name || "—",
      accountId: row.accountId,
      branchId: row.branchId || account?.branchId,
      desc: row.desc || "",
    }));
    return [...givenRows, ...payRows].sort((a, b) => b.date - a.date);
  }, [account, disbursements, payments]);

  const tabEntries = useMemo(() => {
    if (activeTab === 1) return logEntries.filter((r) => r.entryType === "disbursement");
    if (activeTab === 2) return logEntries.filter((r) => r.entryType === "repayment");
    return logEntries;
  }, [logEntries, activeTab]);

  const searchedTabEntries = useMemo(() => {
    const q = logSearch.trim().toLowerCase();
    if (!q) return tabEntries;
    return tabEntries.filter(
      (row) =>
        String(row.desc || "").toLowerCase().includes(q) ||
        String(row.marka || "").toLowerCase().includes(q) ||
        String(row.bichak_no || "").toLowerCase().includes(q)
    );
  }, [tabEntries, logSearch]);

  const tabEntriesWithBalance = useMemo(() => {
    const balanceMap = computeLoanRunningBalances(logEntries);
    return searchedTabEntries.map((row) => ({
      ...row,
      balanceAfter: balanceMap.get(row._id) ?? 0,
    }));
  }, [logEntries, searchedTabEntries]);

  const refreshAll = () => {
    loadLoan();
    dispatch(fetchLoans());
    dispatch(fetchLoanLog());
    dispatch(fetchAccounts());
  };

  if (loading) {
    return (
      <BodyWrapper>
        <FetchingLoading />
      </BodyWrapper>
    );
  }

  if (!account) {
    return (
      <BodyWrapper>
        <Header title="Loan account" desc="Not found" Back />
        <p className="text-center text-muted py-12">Loan account not found.</p>
      </BodyWrapper>
    );
  }

  const balance = getLoanBalance(account);

  return (
    <BodyWrapper>
      <Header title="Loan log" desc={account.partyName} Back>
        <div className="flex flex-wrap items-stretch max850:w-full gap-2 max850:[&>button]:flex-1 max850:[&>button]:min-w-[calc(50%-0.25rem)]">
          <CustomBtn title="Edit party" onClick={() => setOpenEditModal(true)} />
          <CustomBtn title="Give more" onClick={() => setOpenAddLoanModal(true)} />
          {balance > 0 && (
            <CustomBtn title="Receive back" onClick={() => setOpenPaymentModal(true)} />
          )}
        </div>
      </Header>

      <div className="rounded-2xl border border-line bg-card shadow-soft p-4 max850:p-4 sm:p-5 mb-5">
        <div className="flex flex-wrap justify-between gap-3">
          <div>
            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-dangerSoft text-danger">
              {getLoanTypeLabel(account)}
            </span>
            <h2 className="text-xl font-bold text-ink mt-2">{account.partyName}</h2>
            {account.phone && (
              <p className="text-sm text-inkSecondary">{account.phone}</p>
            )}
          </div>
          <div className="text-right">
            <p className="text-xs text-muted uppercase">Status</p>
            <p className="font-bold text-main">{getLoanStatusLabel(account)}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 mt-4 text-sm">
          <div className="rounded-lg bg-subtle p-3">
            <p className="text-muted text-xs">Total given</p>
            <p className="font-bold tabular-nums text-ink">Rs {fmtRs(getLoanPrincipal(account))}</p>
          </div>
          <div className="rounded-lg bg-subtle p-3">
            <p className="text-muted text-xs">Received back</p>
            <p className="font-bold tabular-nums text-success">
              Rs {fmtRs(getLoanPaid(account))}
            </p>
          </div>
          <div className="rounded-lg bg-subtle p-3">
            <p className="text-muted text-xs">Balance due</p>
            <p className="font-bold tabular-nums text-danger">Rs {fmtRs(balance)}</p>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <CustomTabs
          tabs={DETAIL_TABS}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          scrollable
        />
      </div>

      <div className="mb-4">
        <AuthInput
          Value={logSearch}
          setValue={setLogSearch}
          placeholder="Search description, marka, bichak no…"
        />
      </div>

      <LoanTransactionLogCards
        entries={tabEntriesWithBalance}
        showParty={false}
        statementHeader={{
          title: account.partyName,
          subtitle: `${getLoanTypeLabel(account)} loan · Balance Rs ${fmtRs(balance)}`,
        }}
        emptyMessage={
          logSearch.trim()
            ? "No transactions match your search."
            : activeTab === 2
            ? "No repayments recorded yet."
            : activeTab === 1
            ? "No loans given yet."
            : "No transactions yet."
        }
        onEditDisbursement={readOnly ? undefined : setEditDisbursement}
        onEditRepayment={readOnly ? undefined : setEditRepayment}
        onDeleteDisbursement={readOnly ? undefined : setDeleteDisbursement}
        onDeleteRepayment={readOnly ? undefined : setDeletePayment}
      />

      {openAddLoanModal && (
        <AddLoanModal
          Open={openAddLoanModal}
          setOpen={setOpenAddLoanModal}
          existingParty={account}
        />
      )}
      {openPaymentModal && (
        <AddLoanPaymentModal
          open={openPaymentModal}
          setOpen={setOpenPaymentModal}
          loan={account}
          onSuccess={refreshAll}
        />
      )}
      {openEditModal && (
        <EditLoanModal
          open={openEditModal}
          setOpen={setOpenEditModal}
          selectedLoan={account}
        />
      )}
      {editDisbursement && (
        <EditLoanDisbursementModal
          open={!!editDisbursement}
          setOpen={() => setEditDisbursement(null)}
          entry={editDisbursement}
          onSuccess={refreshAll}
        />
      )}
      {editRepayment && (
        <EditLoanPaymentModal
          open={!!editRepayment}
          setOpen={() => setEditRepayment(null)}
          entry={editRepayment}
          onSuccess={refreshAll}
        />
      )}
      {deletePayment && (
        <DeleteModal
          Open={!!deletePayment}
          setOpen={() => setDeletePayment(null)}
          Loading={deleting}
          onSubmit={async () => {
            setDeleting(true);
            try {
              const res = await DeleteLoanPaymentApi(deletePayment._id);
              if (res.data.success) {
                SuccessToast("Repayment removed");
                setDeletePayment(null);
                refreshAll();
              } else ErrorToast(res.data.error?.msg);
            } catch (e) {
              ErrorToast(e.response?.data?.error?.msg || "Delete failed");
            }
            setDeleting(false);
          }}
        />
      )}
      {deleteDisbursement && (
        <DeleteModal
          Open={!!deleteDisbursement}
          setOpen={() => setDeleteDisbursement(null)}
          Loading={deleting}
          onSubmit={async () => {
            setDeleting(true);
            try {
              const res = await DeleteLoanApi(deleteDisbursement._id);
              if (res.data.success) {
                SuccessToast("Entry removed");
                setDeleteDisbursement(null);
                refreshAll();
              } else ErrorToast(res.data.error?.msg);
            } catch (e) {
              ErrorToast(e.response?.data?.error?.msg || "Delete failed");
            }
            setDeleting(false);
          }}
        />
      )}
    </BodyWrapper>
  );
};

export default LoanDetail;
